<template>
  <div style="width: 100%">
    <v-row align="center" style="max-height: 56px">
      <v-col cols="auto">
        <div class="title text--secondary text-uppercase">
          {{ $t("management.healthHazards.title") }}
        </div>
      </v-col>
      <v-col class="text-right">
        <v-btn
          color="primary"
          class="white--text"
          :loading="loading || this.$apollo.loading"
          @click="openNewHealthHazardDialog([], null)"
          v-if="role('admin', 'safetyRepresentative')"
        >
          {{ $t("management.healthHazards.addNew") }}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :items="healthHazardsWithSearch"
      :headers="headers"
      class="safety-elevation-3 table-striped"
      :loading="loading || $apollo.loading"
      :search="search"
      :custom-filter="searchFilter"
      :footer-props="{
        'items-per-page-text': `${$t('employees.footer.itemsPerPage')}`,
        'items-per-page-all-text': `${$t('employees.footer.all')}`,
      }"
    >
      <template v-slot:top>
        <v-text-field
          v-model="search"
          :label="$t('search.title')"
          class="flex-grow-1 pa-3"
          prepend-inner-icon="search"
          single-line
          :background-color="
            $vuetify.theme.themes.light.primary | hexToRgb(0.2)
          "
          solo
          dense
          flat
          hide-details
          clearable
        />
      </template>

      <template v-slot:no-results>{{
        $t("search.noResults", { item: search })
      }}</template>

      <template v-slot:[`item.name`]="{ item }">
        <a
          v-if="role('admin', 'safetyRepresentative')"
          href="#"
          @click="changeHealthHazardTranslation(item.locales, item.id)"
        >
          {{ item.name }}
        </a>
        <span v-else>{{ item.name }}</span>
      </template>

      <template v-slot:[`item.tags`]="{ item }">
        <div>
          <v-chip
            v-for="tag in item.tags"
            :key="tag.id"
            small
            class="ma-1"
            :style="tag.isActive ? '' : 'text-decoration: line-through;'"
          >
            {{ tag.name }}
          </v-chip>
        </div>
      </template>

      <template v-slot:[`item.action`]="{ item }">
        <v-btn
          v-if="role('safetyRepresentative')"
          class="primary"
          small
          @click="openTagDialog(item)"
        >
          {{ $t("management.healthHazards.changeTags") }}
        </v-btn>
      </template>
    </v-data-table>

    <health-hazard-tags-table
      :healthHazards="healthHazards"
      :loading="loading || this.$apollo.loading"
      :tags="tags"
    >
    </health-hazard-tags-table>

    <upsert-tanslation
      v-if="translationDialog"
      :dialogTitle="translationDialog.dialogTitle"
      :id="translationDialog.id"
      :translation="translationDialog.translation"
      :show="translationDialog.show"
      :loading="loading || this.$apollo.loading"
      :healthHazards="healthHazards"
      @hide="translationDialog = null"
      @add="saveHealthHazard(translationDialog.id, $event)"
    />

    <health-hazard-tags-dialog
      v-if="showTagDialog"
      :showTagDialog.sync="showTagDialog"
      :selectedHealthHazard="selectedHealthHazard"
      @save="changeHealthHazardTags"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import UpsertTanslation from "@/components/management/component/UpsertTranslation.vue";
import HealthHazardTagsTable from "@/components/management/component/HealthHazardTagsTable.vue";
import HealthHazardTagsDialog from "@/components/management/component/HealthHazardTagsDialog.vue";

import HealthHazardsQuery from "@/graphql/queries/healthCheck/HealthHazards.gql";
import CreateHealthHazardMutation from "@/graphql/mutation/healthCheck/CreateHealthHazard.gql";
import UpdateHealthHazardMutation from "@/graphql/mutation/healthCheck/UpdateHealthHazard.gql";
import ChangeHealthHazardTagsMutation from "@/graphql/mutation/healthCheck/ChangeHealthHazardTags.gql";
import TagsQuery from "@/graphql/Tags.gql";

export default {
  components: {
    UpsertTanslation,
    HealthHazardTagsTable,
    HealthHazardTagsDialog,
  },
  data() {
    return {
      translationDialog: null,
      showTagDialog: false,
      loading: false,
      selectedHealthHazard: null,
      search: "",
    };
  },
  computed: {
    ...mapGetters(["role"]),
    healthHazardsWithSearch() {
      return (
        this.healthHazards &&
        this.healthHazards.map((hazard) => {
          const tags = hazard.tags || [];

          return {
            ...hazard,
            search: [
              hazard.id,
              hazard.name,
              ...tags.map((tag) => tag.name),
              ...tags.map((tag) => tag.category),
              ...tags.map((tag) => tag.description),
            ]
              .join("|")
              .toLowerCase(),
          };
        })
      );
    },
    headers() {
      return [
        {
          text: "ID",
          value: "id",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("management.healthHazards.name"),
          value: "name",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("management.healthHazards.tags"),
          value: "tags",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("management.healthHazards.action"),
          value: "action",
          class: "text-uppercase subheading text-truncate",
          align: "right",
        },
      ];
    },
  },
  methods: {
    changeHealthHazardTranslation(translation, id) {
      this.translationDialog = {
        show: true,
        translation,
        id,
        allTranslations: [],
        dialogTitle: this.$i18n.t("management.healthHazards.change"),
      };
    },
    openNewHealthHazardDialog(translation, id) {
      this.translationDialog = {
        show: true,
        translation,
        id,
        allTranslations: [],
        dialogTitle: this.$i18n.t("management.healthHazards.addNew"),
      };
    },
    openTagDialog(item) {
      this.selectedHealthHazard = item;
      this.showTagDialog = true;
    },
    saveHealthHazard(id, translations) {
      const locales = translations.map(({ language, text }) => ({
        language,
        text,
      }));

      const mutation = id
        ? UpdateHealthHazardMutation
        : CreateHealthHazardMutation;

      this.$apollo
        .mutate({
          mutation: mutation,
          variables: id ? { id, locales } : { locales },
        })
        .then(() => {
          this.$apollo.queries.healthHazards.refetch();
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            text: err,
            color: "error",
          });
        });
    },
    changeHealthHazardTags(healthHazardId, tagIDs) {
      this.$apollo
        .mutate({
          mutation: ChangeHealthHazardTagsMutation,
          variables: { healthHazardId, tagIDs },
        })
        .then((res) => {
          let updatedHazard = this.healthHazards.find(
            (h) => h.id === healthHazardId
          );
          if (updatedHazard) {
            Object.assign(updatedHazard, res.data.healthHazard);
          }
          this.showTagDialog = false;
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            text: err.message,
            color: "error",
          });
        });
    },
    searchFilter(value, search, item) {
      return (
        !search ||
        search
          .toLowerCase()
          .split(" ")
          .every((w) => item.search.includes(w))
      );
    },
  },
  apollo: {
    healthHazards: {
      query: HealthHazardsQuery,
    },
    tags: {
      query: TagsQuery,
      update: (data) => {
        return data.tags;
      },
    },
  },
};
</script>
